import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { authConfig, authHeaders } from './api/ticketsApi';
import { apiPath } from '../../config/apiBase';
import { useAuth } from '../../App';

const PRODUCTS_API = apiPath('/api/products');

const ProductFilterContext = createContext({
  productId: '',
  setProductId: () => {},
  products: [],
  selectedProduct: null,
  withProductParam: (path) => path,
});

/** Append or replace product_id on a tickets path. */
export function withProductQuery(path, productId) {
  if (!path) return path;
  const [base, qs = ''] = String(path).split('?');
  const params = new URLSearchParams(qs);
  if (productId) params.set('product_id', productId);
  else params.delete('product_id');
  const next = params.toString();
  return next ? `${base}?${next}` : base;
}

export function ProductFilterProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('product_id') || '';
  const [products, setProducts] = useState([]);
  const { user } = useAuth();
  const role = user?.role === 'end_user' ? 'client' : user?.role;
  const isClient = role === 'client';
  // Only super admin sees every product; everyone else only what they're allocated.
  const scoped = !!role && role !== 'super_admin';

  useEffect(() => {
    let cancelled = false;
    const all = axios.get(`${PRODUCTS_API}/active`, authHeaders());
    // Super admin: every product. Everyone else (admin, manager, lead, member,
    // agent): only the products they are allocated to on a product team.
    // Clients additionally keep the Hardware/Software product (IT).
    const own = scoped
      ? axios.get(`${PRODUCTS_API}/active`, authConfig({ allocated_only: true }))
      : null;
    Promise.all([all, own])
      .then(([allRes, ownRes]) => {
        let list = Array.isArray(allRes.data) ? allRes.data : [];
        if (ownRes) {
          const ownIds = new Set((ownRes.data || []).map((p) => p.product_id));
          list = list.filter((p) => ownIds.has(p.product_id)
            || (isClient && (p.handles_domains || []).includes('hardware')));
        }
        if (!cancelled) setProducts(list);
      })
      .catch(() => { if (!cancelled) setProducts([]); });
    return () => { cancelled = true; };
  }, [isClient, scoped]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.product_id === productId) || null,
    [products, productId],
  );

  const setProductId = useCallback((nextId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!nextId) next.delete('product_id');
      else next.set('product_id', nextId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const withProductParam = useCallback(
    (path) => withProductQuery(path, productId),
    [productId],
  );

  const value = useMemo(() => ({
    productId,
    setProductId,
    products,
    selectedProduct,
    withProductParam,
  }), [productId, products, selectedProduct, setProductId, withProductParam]);

  return (
    <ProductFilterContext.Provider value={value}>
      {children}
    </ProductFilterContext.Provider>
  );
}

export function useProductFilter() {
  return useContext(ProductFilterContext);
}
